import { useState, useEffect } from 'react';
import api from '../services/api';
import type { SERP, SERPDetailWithPages, SERPEntryWithPage, PageDetail, SERPCompetitionResponse, CompetitiveSiteInfo, PageKeywordSerpInfo, Page, WebSocketEvent } from '../types';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNetworkEnabled } from '../hooks/useNetworkEnabled';
import { useEscapeKey } from '../hooks/useEscapeKey';

const COMPETITIVE_SCORE_GREAT = 1.3;
const COMPETITIVE_SCORE_GOOD = 1.0;

// While a crawl is pending we poll the backend at this interval so the
// spinner reflects real state (it clears when the crawl finishes or its
// request goes stale) without needing a page reload. The serp_completion
// websocket event is the fast path; this is the backstop.
const SERP_PENDING_POLL_MS = 20 * 1000;

const ENGINE_ICONS: Record<string, { title: string; color: string; path: string }> = {
  GOOGLE: {
    title: 'Google',
    color: '#4285F4',
    path: 'M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z',
  },
  BING: {
    title: 'Bing',
    color: '#008373',
    path: 'M2.01 0L0 2.973l8.318 4.476v9.067l6.493 3.484 9.179-4.979V8.164l-5.59 2.991v3.211l-3.589 1.937V9.542l-9.8-5.27z',
  },
  DUCKDUCKGO: {
    title: 'DuckDuckGo',
    color: '#DE5833',
    path: 'M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0zm0 .984C18.083.984 23.016 5.916 23.016 12S18.084 23.016 12 23.016.984 18.084.984 12C.984 5.917 5.916.984 12 .984zm0 .938C6.434 1.922 1.922 6.434 1.922 12c0 4.437 2.867 8.205 6.85 9.55-.237-.82-.776-2.753-1.6-6.052-1.184-4.741-2.064-8.606 2.379-9.813.047-.011.064-.064.03-.093-.514-.467-1.382-.548-2.233-.38a.06.06 0 0 1-.07-.058c0-.011 0-.023.011-.035.205-.286.572-.507.822-.64a1.843 1.843 0 0 0-.607-.335c-.059-.022-.059-.12-.006-.144.006-.006.012-.012.024-.012 1.749-.233 3.586.292 4.49 1.448.011.011.023.017.035.023 2.968.635 3.509 4.837 3.328 5.998a9.607 9.607 0 0 0 2.346-.576c.746-.286 1.008-.222 1.101-.053.1.193-.018.513-.28.81-.496.567-1.393 1.01-2.974 1.137-.546.044-1.029.024-1.445.006-.789-.035-1.339-.059-1.633.39-.192.298-.041.998 1.487 1.22 1.09.157 2.078.047 2.798-.034.643-.07 1.073-.118 1.172.069.21.402-.996 1.207-3.066 1.224-.158 0-.315-.006-.467-.011-1.283-.065-2.227-.414-2.816-.735a.094.094 0 0 1-.035-.017c-.105-.059-.31.045-.188.267.07.134.444.478 1.004.776-.058.466.087 1.184.338 2l.088-.016c.041-.009.087-.019.134-.025.507-.082.775.012.926.175.717-.536 1.913-1.294 2.03-1.154.583.694.66 2.332.53 2.99-.004.012-.017.024-.04.035-.274.117-1.783-.296-1.783-.511-.059-1.075-.26-1.173-.493-1.225h-.156c.006.006.012.018.018.03l.052.12c.093.257.24 1.063.13 1.26-.112.199-.835.297-1.284.303-.443.006-.543-.158-.637-.408-.07-.204-.103-.675-.103-.95a.857.857 0 0 1 .012-.216c-.134.058-.333.193-.397.281-.017.262-.017.682.123 1.149.07.221-1.518 1.164-1.74.99-.227-.181-.634-1.952-.459-2.67-.187.017-.338.075-.42.191-.367.508.093 2.933.582 3.248.257.169 1.54-.553 2.176-1.095.105.145.305.158.553.158.326-.012.782-.06 1.103-.158.192.45.423.972.613 1.388 4.47-1.032 7.803-5.037 7.803-9.82 0-5.566-4.512-10.078-10.078-10.078zm1.791 5.646c-.42 0-.678.146-.795.332-.023.047.047.094.094.07.14-.075.357-.161.701-.156.328.006.516.09.67.159l.023.01c.041.017.088-.03.059-.065-.134-.18-.332-.35-.752-.35zm-5.078.198a1.24 1.24 0 0 0-.522.082c-.454.169-.67.526-.67.76 0 .051.112.057.141.011.081-.123.21-.31.617-.478.408-.17.73-.146.951-.094.047.012.083-.041.041-.07a.989.989 0 0 0-.558-.211zm5.434 1.423a.651.651 0 0 0-.655.647.652.652 0 0 0 1.307 0 .646.646 0 0 0-.652-.647zm.283.262h.008a.17.17 0 0 1 .17.17c0 .093-.077.17-.17.17a.17.17 0 0 1-.17-.17c0-.09.072-.165.162-.17zm-5.358.076a.752.752 0 0 0-.758.758c0 .42.338.758.758.758s.758-.337.758-.758a.756.756 0 0 0-.758-.758zm.328.303h.01c.112 0 .2.089.2.2 0 .11-.088.197-.2.197a.195.195 0 0 1-.197-.198c0-.107.082-.194.187-.199z',
  },
};

function EngineIcon({ engine, size = 'md' }: { engine: string | null; size?: 'sm' | 'md' | 'lg' }) {
  const key = engine?.toUpperCase() || '';
  const icon = ENGINE_ICONS[key];
  const sizeClasses = { sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8' };
  const glyphSizes = { sm: 'h-2.5 w-2.5', md: 'h-3.5 w-3.5', lg: 'h-5 w-5' };

  if (!icon) {
    return (
      <span className={`inline-flex ${sizeClasses[size]} items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300`}>
        {key ? key.charAt(0) : '?'}
      </span>
    );
  }

  return (
    <span className={`inline-flex ${sizeClasses[size]} items-center justify-center rounded-full bg-white dark:bg-gray-800 shadow-sm ring-1 ring-gray-200`}>
      <svg viewBox="0 0 24 24" className={glyphSizes[size]} fill={icon.color}>
        <path d={icon.path} />
      </svg>
    </span>
  );
}

function getCompetitorBadgeColor(competitive_score: number): string {
  if (competitive_score >= COMPETITIVE_SCORE_GREAT) return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 ring-green-300';
  if (competitive_score >= COMPETITIVE_SCORE_GOOD) return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 ring-amber-300';
  return 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 ring-orange-300';
}


function getProcessingStateLabel(state: number): string {
  switch (state) {
    case 0: return 'Not processed';
    case 1: return 'Error';
    case 2: return 'Summarized';
    default: return 'Unknown';
  }
}

function getProcessingStateColor(state: number): string {
  switch (state) {
    case 0: return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
    case 1: return 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400';
    case 2: return 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
  }
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

function Spinner({ className = 'w-3 h-3' }: { className?: string }) {
  return (
    <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

// ─── Observed-data disclaimer ─────────────────────────────────────────────────

function ObservedDataNote() {
  return (
    <p className="text-[10px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1 mb-2.5 leading-snug">
      Observed data only - reflects what has been collected within your project and may not be comprehensive.
    </p>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

interface SerpModalProps {
  isOpen: boolean;
  onClose: () => void;
  keywordId: number;
  keywordValue: string;
  topicKeywordId?: number;
}

export default function SerpModal({ isOpen, onClose, keywordId, keywordValue, topicKeywordId }: SerpModalProps) {
  useEscapeKey(isOpen, onClose);
  const [serps, setSerps] = useState<SERP[]>([]);
  const [selectedSerp, setSelectedSerp] = useState<SERPDetailWithPages | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());
  const [pageContents, setPageContents] = useState<Record<number, PageDetail>>({});
  const [loadingPageContent, setLoadingPageContent] = useState<Set<number>>(new Set());
  const [competition, setCompetition] = useState<SERPCompetitionResponse | null>(null);
  const [savingReference, setSavingReference] = useState<number | null>(null);
  const [savedEntries, setSavedEntries] = useState<Set<number>>(new Set());
  const [requestingSerp, setRequestingSerp] = useState(false);
  const [serpPending, setSerpPending] = useState(false);
  const [extractingPages, setExtractingPages] = useState<Set<number>>(new Set());
  const [extractErrors, setExtractErrors] = useState<Record<number, string>>({});
  const [ensuring, setEnsuring] = useState(false);
  const [ensureNote, setEnsureNote] = useState<string | null>(null);

  const { subscribe } = useWebSocket();
  const networkEnabled = useNetworkEnabled();

  const handleRequestSerp = async () => {
    if (!networkEnabled) return;
    setRequestingSerp(true);
    try {
      // force=true: the refresh button re-crawls + re-extracts every
      // result page, not just missing/failed ones.
      await api.createSerpRequest(keywordId, true);
      setSerpPending(true);
    } catch (err) {
      console.error('Failed to request SERP:', err);
    } finally {
      setRequestingSerp(false);
    }
  };

  // Cancel a stuck/in-flight crawl: release the lock so the spinner clears
  // at once (backend also broadcasts serp_completion to other views). Unlike
  // the refresh button — which force-restarts the crawl — this stops it.
  const handleCancelSerp = async () => {
    setSerpPending(false);
    try {
      await api.cancelSerpRequest(keywordId);
    } catch (err) {
      console.error('Failed to cancel SERP request:', err);
    }
  };

  const handleSaveReference = async (entryId: number) => {
    if (!topicKeywordId) return;
    setSavingReference(entryId);
    try {
      await api.createDocumentFromSerpEntry({
        serp_entry_id: entryId,
        topic_keyword_id: topicKeywordId,
      });
      setSavedEntries(prev => new Set(prev).add(entryId));
    } catch (err) {
      console.error('Failed to save reference:', err);
    } finally {
      setSavingReference(null);
    }
  };

  // Re-run fact extraction for one already-crawled page. The crawl paths defer
  // extraction and the daemon only walks active topics, so a page can sit with
  // content but no facts and nothing that will ever retry it. Facts stream back
  // over page_facts_extracted; the terminal event (done) clears the spinner.
  const handleExtractFacts = async (pageId: number) => {
    setExtractErrors(prev => {
      const next = { ...prev };
      delete next[pageId];
      return next;
    });
    setExtractingPages(prev => new Set(prev).add(pageId));
    try {
      await api.extractPageFacts(pageId);
    } catch (err) {
      setExtractingPages(prev => { const next = new Set(prev); next.delete(pageId); return next; });
      setExtractErrors(prev => ({
        ...prev,
        [pageId]: err instanceof Error ? err.message : 'Could not start extraction',
      }));
    }
  };

  // Bulk form of the per-page Extract button: sweep every ranking page for
  // this keyword that still has no facts. The backend hands back exactly the
  // pages it will work on, so each row shows its own spinner and clears on its
  // own terminal event — no opaque modal-wide progress bar.
  const handleEnsureExtracted = async () => {
    setEnsuring(true);
    setEnsureNote(null);
    try {
      const res = await api.ensureKeywordExtracted(keywordId);
      if (!res.queued) {
        setEnsureNote('Every result already has facts.');
      } else {
        setExtractingPages(prev => {
          const next = new Set(prev);
          res.page_ids.forEach(id => next.add(id));
          return next;
        });
      }
    } catch (err) {
      setEnsureNote(err instanceof Error ? err.message : 'Could not start extraction');
    } finally {
      setEnsuring(false);
    }
  };

  const loadSerps = async () => {
    setLoading(true);
    setSelectedSerp(null);
    try {
      const data = await api.getSerpsByKeyword(keywordId);
      setSerps(data);
      if (data.length > 0) loadSerpDetails(data[0].id);
    } catch (err) {
      console.error('Failed to load SERPs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && keywordId) {
      loadSerps();
      // Derive the in-progress spinner from the backend, not just in-memory
      // websocket state — otherwise a page reload mid-crawl shows no spinner.
      api
        .getSerpPending(keywordId)
        .then((r) => setSerpPending(r.pending))
        .catch(() => {});
    }
  }, [isOpen, keywordId]); // eslint-disable-line react-hooks/exhaustive-deps

  // While pending, poll the backend so the spinner reflects reality: it
  // clears when the crawl finishes (fast path: the serp_completion event
  // below) OR when the request goes stale (worker died) — without a reload,
  // and without clearing early on a still-running crawl.
  useEffect(() => {
    if (!serpPending || !keywordId) return;
    const id = setInterval(async () => {
      try {
        const r = await api.getSerpPending(keywordId);
        if (!r.pending) {
          setSerpPending(false);
          loadSerps();
        }
      } catch {
        /* transient — keep polling */
      }
    }, SERP_PENDING_POLL_MS);
    return () => clearInterval(id);
  }, [serpPending, keywordId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type === 'serp_requested' && event.keyword_ids.includes(keywordId)) {
        setSerpPending(true);
      } else if (event.event_type === 'serp_completion' && event.keyword_ids.includes(keywordId)) {
        setSerpPending(false);
        loadSerps();
      } else if (event.event_type === 'page_facts_extracted') {
        // The last event of a run carries done + the settled processing_state;
        // release the Extract button's spinner on it.
        if (event.done) {
          setExtractingPages(prev => { const next = new Set(prev); next.delete(event.page_id); return next; });
        }
        // Facts stream in per chunk during extraction — patch the matching
        // entry's page in place so the Facts tab updates live without a reload.
        setSelectedSerp(prev => {
          if (!prev) return prev;
          if (!prev.entries.some(e => e.page_id === event.page_id)) return prev;
          return {
            ...prev,
            entries: prev.entries.map(e =>
              e.page_id === event.page_id && e.page
                ? {
                    ...e,
                    page: {
                      ...e.page,
                      facts: event.facts,
                      ai_summary: event.ai_summary ?? e.page.ai_summary,
                      ...(event.processing_state != null
                        ? { processing_state: event.processing_state }
                        : {}),
                    },
                  }
                : e
            ),
          };
        });
      }
    });
  }, [isOpen, keywordId, subscribe]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSerpDetails = async (serpId: number) => {
    setLoadingDetails(true);
    try {
      const [data, competitionData] = await Promise.all([
        api.getSerpWithPages(serpId),
        api.getManagedSiteComparison(serpId).catch(() => null),
      ]);
      setSelectedSerp(data);
      setCompetition(competitionData);
      setExpandedEntries(new Set());
      setPageContents({});
      setLoadingPageContent(new Set());
    } catch (err) {
      console.error('Failed to load SERP details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadPageContent = async (pageId: number) => {
    if (pageContents[pageId] || loadingPageContent.has(pageId)) return;
    setLoadingPageContent(prev => new Set(prev).add(pageId));
    try {
      const data = await api.getPage(pageId);
      setPageContents(prev => ({ ...prev, [pageId]: data }));
    } catch (err) {
      console.error('Failed to load page content:', err);
    } finally {
      setLoadingPageContent(prev => { const next = new Set(prev); next.delete(pageId); return next; });
    }
  };

  const toggleExpanded = (entryId: number) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      next.has(entryId) ? next.delete(entryId) : next.add(entryId);
      return next;
    });
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (!isOpen) return null;

  const organicEntries = selectedSerp?.entries.filter(e => !e.is_ad) ?? [];
  const adEntries = selectedSerp?.entries.filter(e => e.is_ad) ?? [];
  // What "Ensure extracted" has to work on, and what's in flight right now.
  // Counted off the loaded entries so the header answers "is my research
  // actually complete?" without a second request.
  const missingFacts = organicEntries.filter(e => !(e.page?.facts?.length)).length;
  const extractingCount = organicEntries.filter(
    e => e.page_id && extractingPages.has(e.page_id),
  ).length;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="hidden sm:block fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

      <div className="fixed inset-0 sm:inset-y-0 sm:right-0 sm:left-auto flex sm:max-w-full sm:pl-10">
        <div className="w-full sm:w-screen sm:max-w-3xl">
          <div className="flex h-full flex-col bg-white dark:bg-gray-800 shadow-xl">

            {/* ── Header ── */}
            <div className="px-4 py-3 sm:px-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">SERP Results</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{keywordValue}</h2>
                    {selectedSerp?.url && (
                      <a
                        href={selectedSerp.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open search"
                        className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                    {/* Always clickable — even while a crawl is in progress
                        (spinning) a press force-restarts it, so a stuck
                        spinner can never block the user. */}
                    <button
                      onClick={handleRequestSerp}
                      disabled={requestingSerp || !networkEnabled}
                      title={
                        !networkEnabled
                          ? 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'
                          : serpPending
                          ? 'A crawl is in progress - click to restart it.'
                          : 'Refresh SERP'
                      }
                      className={`flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                        serpPending ? 'text-amber-500' : 'text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400'
                      }`}
                    >
                      <svg className={`h-3.5 w-3.5 ${serpPending || requestingSerp ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    {/* Cancel the in-flight crawl (only while one is pending).
                        Distinct from refresh, which force-restarts. */}
                    {serpPending && (
                      <button
                        onClick={handleCancelSerp}
                        title="Cancel this SERP crawl"
                        aria-label="Cancel SERP crawl"
                        className="flex-shrink-0 text-amber-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Extraction coverage for the loaded SERP. A crawled page with
                  no facts is invisible to research, and nothing automatic
                  retries one that ERRORed — so surface the gap here, next to
                  the results, with the one press that closes it. */}
              {selectedSerp && organicEntries.length > 0 && (
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleEnsureExtracted}
                    disabled={ensuring || extractingCount > 0 || missingFacts === 0}
                    title={
                      missingFacts === 0
                        ? 'Every result already has extracted facts'
                        : `Extract facts for the ${missingFacts} result${missingFacts !== 1 ? 's' : ''} still missing them`
                    }
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {extractingCount > 0 ? (
                      <><Spinner /> Extracting {extractingCount}…</>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-6.714 2.143L12 21l-2.286-6.857L3 12l6.714-2.143L12 3z" />
                        </svg>
                        Ensure extracted
                        {missingFacts > 0 && ` (${missingFacts})`}
                      </>
                    )}
                  </button>
                  {missingFacts === 0 && extractingCount === 0 && (
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                      All {organicEntries.length} results have facts
                    </span>
                  )}
                  {ensureNote && (
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">{ensureNote}</span>
                  )}
                </div>
              )}

              {/* SERP date/engine selector */}
              {serps.length > 0 && (
                <div className="mt-2.5 flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
                  {serps.map((serp) => (
                    <button
                      key={serp.id}
                      onClick={() => loadSerpDetails(serp.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                        selectedSerp?.id === serp.id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      <EngineIcon engine={serp.search_engine} size="sm" />
                      {formatDate(serp.collected_at)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Content ── */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32 gap-2 text-gray-500 dark:text-gray-400 text-sm">
                  <Spinner className="w-4 h-4" /> Loading…
                </div>
              ) : serps.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-500">
                  <svg className="w-10 h-10 mb-2 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-sm">No SERP data collected for this keyword</p>
                </div>
              ) : loadingDetails ? (
                <div className="flex items-center justify-center h-32 gap-2 text-gray-500 dark:text-gray-400 text-sm">
                  <Spinner className="w-4 h-4" /> Loading results…
                </div>
              ) : selectedSerp ? (
                <div className="px-3 py-3 sm:px-5 sm:py-4 space-y-2">

                  {/* Organic results */}
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      Organic · {organicEntries.length} results
                    </span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">
                      {formatDate(selectedSerp.collected_at)}
                    </span>
                  </div>

                  {organicEntries.map((entry) => (
                    <SerpEntryCard
                      key={entry.id}
                      entry={entry}
                      isExpanded={expandedEntries.has(entry.id)}
                      onToggle={() => toggleExpanded(entry.id)}
                      pageContent={entry.page_id ? pageContents[entry.page_id] : undefined}
                      isLoadingContent={entry.page_id ? loadingPageContent.has(entry.page_id) : false}
                      onLoadContent={() => entry.page_id && loadPageContent(entry.page_id)}
                      competitors={entry.page?.domain_id ? competition?.competitions[String(entry.page.domain_id)] ?? [] : []}
                      topicKeywordId={topicKeywordId}
                      savingReference={savingReference}
                      savedEntries={savedEntries}
                      onSaveReference={handleSaveReference}
                      isExtracting={entry.page_id ? extractingPages.has(entry.page_id) : false}
                      extractError={entry.page_id ? extractErrors[entry.page_id] : undefined}
                      onExtractFacts={() => entry.page_id && handleExtractFacts(entry.page_id)}
                    />
                  ))}

                  {/* Paid results */}
                  {adEntries.length > 0 && (
                    <>
                      <div className="pt-2">
                        <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                          Paid · {adEntries.length} result{adEntries.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {adEntries.map((entry) => (
                        <SerpEntryCard
                          key={entry.id}
                          entry={entry}
                          isExpanded={expandedEntries.has(entry.id)}
                          onToggle={() => toggleExpanded(entry.id)}
                          pageContent={entry.page_id ? pageContents[entry.page_id] : undefined}
                          isLoadingContent={entry.page_id ? loadingPageContent.has(entry.page_id) : false}
                          onLoadContent={() => entry.page_id && loadPageContent(entry.page_id)}
                          competitors={entry.page?.domain_id ? competition?.competitions[String(entry.page.domain_id)] ?? [] : []}
                          topicKeywordId={topicKeywordId}
                          savingReference={savingReference}
                          savedEntries={savedEntries}
                          onSaveReference={handleSaveReference}
                          isExtracting={entry.page_id ? extractingPages.has(entry.page_id) : false}
                          extractError={entry.page_id ? extractErrors[entry.page_id] : undefined}
                          onExtractFacts={() => entry.page_id && handleExtractFacts(entry.page_id)}
                        />
                      ))}
                    </>
                  )}
                </div>
              ) : null}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Extract-facts action ─────────────────────────────────────────────────────

// One-click re-run of LLM fact/summary extraction for a page that was crawled
// but never extracted (or whose extraction errored). Rendered on every tab that
// can show the gap — Summary, Facts, Content — since the same press fixes all
// three.
function ExtractFactsButton({ hasFacts, isExtracting, error, onExtract }: {
  hasFacts: boolean;
  isExtracting: boolean;
  error?: string;
  onExtract: () => void;
}) {
  return (
    <div>
      <button
        onClick={e => { e.stopPropagation(); onExtract(); }}
        disabled={isExtracting}
        title="Run the extraction model over this page's stored content"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isExtracting ? (
          <><Spinner /> Extracting…</>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-6.714 2.143L12 21l-2.286-6.857L3 12l6.714-2.143L12 3z" />
            </svg>
            {hasFacts ? 'Re-extract facts' : 'Extract facts'}
          </>
        )}
      </button>
      {error && <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

// ─── Entry card ───────────────────────────────────────────────────────────────

type TabId = 'summary' | 'facts' | 'keywords' | 'domain' | 'content';

interface SerpEntryCardProps {
  entry: SERPEntryWithPage;
  isExpanded: boolean;
  onToggle: () => void;
  pageContent?: PageDetail;
  isLoadingContent: boolean;
  onLoadContent: () => void;
  competitors: CompetitiveSiteInfo[];
  topicKeywordId?: number;
  savingReference: number | null;
  savedEntries: Set<number>;
  onSaveReference: (entryId: number) => void;
  isExtracting: boolean;
  extractError?: string;
  onExtractFacts: () => void;
}

function SerpEntryCard({ entry, isExpanded, onToggle, pageContent, isLoadingContent, onLoadContent, competitors, topicKeywordId, savingReference, savedEntries, onSaveReference, isExtracting, extractError, onExtractFacts }: SerpEntryCardProps) {
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [pageKeywords, setPageKeywords] = useState<PageKeywordSerpInfo[] | null>(null);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [domainPages, setDomainPages] = useState<Page[] | null>(null);
  const [loadingDomainPages, setLoadingDomainPages] = useState(false);

  useEffect(() => {
    if (!isExpanded) return;
    setActiveTab('summary');

    if (entry.page_id && pageKeywords === null && !loadingKeywords) {
      setLoadingKeywords(true);
      api.getPageKeywords(entry.page_id)
        .then(data => setPageKeywords(data))
        .catch(() => setPageKeywords([]))
        .finally(() => setLoadingKeywords(false));
    }

    if (entry.page?.domain_id && domainPages === null && !loadingDomainPages) {
      setLoadingDomainPages(true);
      api.getDomainPages(entry.page.domain_id)
        .then(data => setDomainPages(data.items.filter(p => p.id !== entry.page_id)))
        .catch(() => setDomainPages([]))
        .finally(() => setLoadingDomainPages(false));
    }
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasSummary = !!(entry.page?.ai_summary && entry.page.ai_summary.length > 0);
  const hasContent = !!(pageContent?.content || pageContent?.extracted_text);
  const facts = entry.page?.facts ?? [];

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'summary', label: 'Summary' },
    { id: 'facts',    label: 'Facts',    count: facts.length },
    { id: 'keywords', label: 'Keywords', count: pageKeywords?.length },
    { id: 'domain',   label: 'Domain',   count: domainPages?.length },
    { id: 'content',  label: 'Content' },
  ];

  return (
    <div className={`border rounded-lg overflow-hidden ${entry.is_ad ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/30' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>

      {/* ── Card header (always visible) ── */}
      <div
        className="px-3 py-2.5 sm:px-4 sm:py-3 cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-800 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start gap-2.5">

          {/* Position badge */}
          <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${
            entry.is_ad        ? 'bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300'
            : entry.position <= 3  ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
            : entry.position <= 10 ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          }`}>
            {entry.is_ad ? 'Ad' : entry.position}
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-indigo-600 dark:text-indigo-400 line-clamp-1 leading-snug">
              {entry.title}
            </h3>
            {entry.page?.url && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">{entry.page.url}</p>
            )}

            {/* Domain metrics + competitor badges */}
            {!entry.is_ad && (entry.page?.domain_metrics || competitors.length > 0) && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {entry.page?.domain_metrics && (
                  <>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded" title="OPR Overall Rank">
                      OPR {entry.page.domain_metrics.opr_overall_rank.toLocaleString()}
                    </span>
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded" title="OPR Page Rank">
                      PR {entry.page.domain_metrics.opr_page_rank.toLocaleString()}
                    </span>
                    {entry.page.domain_metrics.referring_domains != null && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded" title="Referring Domains">
                        {entry.page.domain_metrics.referring_domains.toLocaleString()} RD
                      </span>
                    )}
                  </>
                )}
                {competitors.map(c => (
                  <span
                    key={c.site_id}
                    className={`px-1.5 py-0.5 text-[10px] font-medium rounded ring-1 ${getCompetitorBadgeColor(c.competitive_score)}`}
                    title={`${c.site_name}: OPR ${c.opr.toLocaleString()}, PR ${c.pr.toLocaleString()}`}
                  >
                    {c.site_name}
                  </span>
                ))}
              </div>
            )}

            {entry.description && (
              <p className="hidden sm:block text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 leading-snug">
                {entry.description}
              </p>
            )}
          </div>

          {/* Chevron */}
          <div className="flex-shrink-0 flex items-center gap-1.5 mt-0.5">
            {hasSummary && !isExpanded && (
              <span className="hidden sm:inline px-1.5 py-0.5 text-[10px] font-medium bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded">AI</span>
            )}
            <svg
              className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-150 ${isExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* ── Expanded panel ── */}
      {isExpanded && entry.page && (
        <div className="border-t border-gray-100 dark:border-gray-700">

          {/* Info bar: domain · page type · state · open link */}
          <div className="px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              {entry.page.domain_name && (
                <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 truncate max-w-[180px]" title={entry.page.domain_name}>
                  {entry.page.domain_name}
                </span>
              )}
              {entry.page.page_type && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded flex-shrink-0">
                  {entry.page.page_type}
                </span>
              )}
              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded flex-shrink-0 ${getProcessingStateColor(entry.page.processing_state)}`}>
                {getProcessingStateLabel(entry.page.processing_state)}
              </span>
            </div>
            <a
              href={entry.page.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
            >
              Open page
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* Tab strip */}
          {/* overflow-y-hidden pairs with overflow-x-auto — without it the
              tabs' `-mb-px` overflows the content box by a pixel and the strip
              grows a vertical scrollbar. */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex overflow-x-auto overflow-y-hidden">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={e => { e.stopPropagation(); setActiveTab(tab.id); }}
                className={`px-3.5 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
                {tab.count != null && (
                  <span className={`text-[10px] min-w-[16px] px-1 rounded-full font-semibold text-center ${
                    activeTab === tab.id ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="px-3 sm:px-4 py-3 bg-gray-50 dark:bg-gray-800 min-h-[72px]">

            {/* Summary */}
            {activeTab === 'summary' && (
              hasSummary
                ? <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{entry.page.ai_summary}</p>
                    {topicKeywordId && (
                      <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={(e) => { e.stopPropagation(); onSaveReference(entry.id); }}
                          disabled={savingReference === entry.id || savedEntries.has(entry.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingReference === entry.id ? 'Saving...' : savedEntries.has(entry.id) ? 'Saved' : 'Save as Reference'}
                        </button>
                      </div>
                    )}
                  </div>
                : <div className="space-y-2.5">
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">No AI summary available for this page.</p>
                    <ExtractFactsButton
                      hasFacts={facts.length > 0}
                      isExtracting={isExtracting}
                      error={extractError}
                      onExtract={onExtractFacts}
                    />
                  </div>
            )}

            {/* Extracted facts */}
            {activeTab === 'facts' && (
              facts.length > 0
                ? <div>
                    <ul className="space-y-1.5">
                      {facts.map((fact, i) => (
                        <li key={i} className="flex gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                          <span className="flex-shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-400" />
                          <span>{fact}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-start gap-2">
                      {topicKeywordId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSaveReference(entry.id); }}
                          disabled={savingReference === entry.id || savedEntries.has(entry.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingReference === entry.id ? 'Saving...' : savedEntries.has(entry.id) ? 'Saved' : 'Save as Reference'}
                        </button>
                      )}
                      <ExtractFactsButton
                        hasFacts
                        isExtracting={isExtracting}
                        error={extractError}
                        onExtract={onExtractFacts}
                      />
                    </div>
                  </div>
                : <div className="space-y-2.5">
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">No facts extracted for this page.</p>
                    <ExtractFactsButton
                      hasFacts={false}
                      isExtracting={isExtracting}
                      error={extractError}
                      onExtract={onExtractFacts}
                    />
                  </div>
            )}

            {/* Keywords ranking this page */}
            {activeTab === 'keywords' && (
              <div>
                <ObservedDataNote />
                {loadingKeywords ? (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500"><Spinner /> Loading…</div>
                ) : pageKeywords && pageKeywords.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {pageKeywords.map(kw => (
                      <span
                        key={kw.keyword_id}
                        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded border border-gray-200 dark:border-gray-700"
                        title={`${kw.serp_count} SERP${kw.serp_count !== 1 ? 's' : ''} · last seen ${kw.latest_serp_date ? new Date(kw.latest_serp_date).toLocaleDateString() : 'unknown'}`}
                      >
                        {kw.latest_position != null && (
                          <span className={`text-[10px] font-bold tabular-nums ${
                            kw.latest_position <= 3  ? 'text-green-600 dark:text-green-400'
                            : kw.latest_position <= 10 ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-400 dark:text-gray-500'
                          }`}>
                            #{kw.latest_position}
                          </span>
                        )}
                        <span>{kw.keyword_value}</span>
                        {kw.search_engine && <EngineIcon engine={kw.search_engine} size="sm" />}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No keyword SERP data observed for this page.</p>
                )}
              </div>
            )}

            {/* Other pages on this domain */}
            {activeTab === 'domain' && (
              <div>
                <ObservedDataNote />
                {loadingDomainPages ? (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500"><Spinner /> Loading…</div>
                ) : domainPages && domainPages.length > 0 ? (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                    {domainPages.map(p => (
                      <div key={p.id} className="flex items-center gap-1.5">
                        {p.page_type && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">
                            {p.page_type}
                          </span>
                        )}
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 truncate"
                          title={p.title ?? p.url}
                        >
                          {p.title || p.url}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 italic">No other pages observed for this domain.</p>
                )}
              </div>
            )}

            {/* Full content */}
            {activeTab === 'content' && (
              <div>
                {!hasContent && !isLoadingContent && (
                  <button
                    onClick={e => { e.stopPropagation(); onLoadContent(); }}
                    className="inline-flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Load full content
                  </button>
                )}
                {isLoadingContent && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500"><Spinner /> Loading content…</div>
                )}
                {hasContent && (
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                      {pageContent?.extracted_text ? 'Extracted Text' : 'Raw Content'}
                    </p>
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded p-3 max-h-72 overflow-y-auto">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                        {pageContent?.extracted_text || pageContent?.content}
                      </pre>
                    </div>
                    {/* Content but no facts is the common gap — the crawl path
                        defers extraction and only active topics get retried. */}
                    <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-start gap-2">
                      {topicKeywordId && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onSaveReference(entry.id); }}
                          disabled={savingReference === entry.id || savedEntries.has(entry.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {savingReference === entry.id ? 'Saving...' : savedEntries.has(entry.id) ? 'Saved' : 'Save as Reference'}
                        </button>
                      )}
                      <ExtractFactsButton
                        hasFacts={facts.length > 0}
                        isExtracting={isExtracting}
                        error={extractError}
                        onExtract={onExtractFacts}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
